// src/pages/Sessions/Sessions.tsx
import React, { useState, useEffect } from "react";
import {
  PowerIcon,
  ShieldCheckIcon,
  UsersIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import Button from "../../components/UI/Button";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../api/firebase/config";
import ConfirmationModal from "../../components/UI/ConfirmationModal";

interface ForceUpdateInfo {
  force_logout: boolean;
  updated_at: Date;
}

const Sessions: React.FC = () => {
  const [forceLogoutEnabled, setForceLogoutEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [affectedUsers, setAffectedUsers] = useState<number>(0);
  const [updatingProgress, setUpdatingProgress] = useState<{
    current: number;
    total: number;
    completed: boolean;
  } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<boolean>(false);

  const forceUpdateDocRef = doc(db, "app_settings", "force_update_info");
  const usersCollection = collection(db, "users");

  useEffect(() => {
    fetchForceUpdateInfo();
    countAffectedUsers();
  }, []);

  const fetchForceUpdateInfo = async () => {
    setLoading(true);
    setError("");
    try {
      const docSnap = await getDoc(forceUpdateDocRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as ForceUpdateInfo;
        setForceLogoutEnabled(data.force_logout === true);

        if (data.updated_at) {
          const date = new Date(data.updated_at);
          setLastUpdated(date.toLocaleString());
        }
      } else {
        await createDefaultDocument();
      }
    } catch (err: any) {
      console.error("Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const countAffectedUsers = async () => {
    try {
      const usersSnapshot = await getDocs(usersCollection);
      const usersWithKey = usersSnapshot.docs.filter(
        (doc) => "is_first_time" in doc.data(),
      ).length;
      setAffectedUsers(usersWithKey);
    } catch (err) {
      console.error("Error counting users:", err);
    }
  };

  const createDefaultDocument = async () => {
    const defaultData: ForceUpdateInfo = {
      force_logout: false,
      updated_at: new Date(),
    };

    await setDoc(forceUpdateDocRef, defaultData);
    setForceLogoutEnabled(false);
    setLastUpdated(new Date().toLocaleString());
  };

  const updateAllUsers = async (newValue: boolean) => {
    try {
      const usersSnapshot = await getDocs(usersCollection);
      const usersWithKey = usersSnapshot.docs.filter(
        (doc) => "is_first_time" in doc.data(),
      );

      let completed = 0;
      const total = usersWithKey.length;

      setUpdatingProgress({ current: 0, total, completed: false });

      const updatePromises = usersWithKey.map(async (userDoc, index) => {
        await updateDoc(doc(db, "users", userDoc.id), {
          is_first_time: newValue,
          updated_at: new Date(),
        });

        completed++;
        setUpdatingProgress({ current: completed, total, completed: false });
      });

      await Promise.all(updatePromises);
      setUpdatingProgress({ current: total, total, completed: true });
      return total;
    } catch (err) {
      console.error("Error updating users:", err);
      setUpdatingProgress(null);
      throw err;
    }
  };

  const handleToggle = async () => {
    const newValue = !forceLogoutEnabled;
    setPendingAction(newValue);
    setShowConfirmModal(true);
  };

  const confirmToggle = async () => {
    const newValue = pendingAction;
    const action = newValue ? "enable" : "disable";

    setSaving(true);
    setError("");
    setUpdatingProgress(null);
    setShowConfirmModal(false);

    try {
      const updatedData: Partial<ForceUpdateInfo> = {
        force_logout: newValue,
        updated_at: new Date(),
      };

      await setDoc(forceUpdateDocRef, updatedData, { merge: true });

      setForceLogoutEnabled(newValue);
      setLastUpdated(new Date().toLocaleString());

      const updatedCount = await updateAllUsers(newValue);

      // Show success message (you might want to use a toast notification here)
      console.log(
        `✓ Force logout ${action}d successfully. Updated ${updatedCount} users.`,
      );

      await countAffectedUsers();
    } catch (err: any) {
      console.error("Error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
      setTimeout(() => {
        setUpdatingProgress(null);
      }, 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Session Control
            </h1>
            <p className="text-gray-600 mt-1">
              Manage user authentication settings
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Progress Indicator */}
      {updatingProgress && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <LoadingSpinner size="sm" color="blue" />
              <span className="text-sm font-medium text-blue-800 ml-2">
                Updating users...
              </span>
            </div>
            <span className="text-sm text-blue-600">
              {updatingProgress.current} / {updatingProgress.total}
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(updatingProgress.current / updatingProgress.total) * 100}%`,
              }}
            />
          </div>
          {updatingProgress.completed && (
            <div className="flex items-center mt-2 text-green-600 text-sm">
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              Update completed successfully
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <UsersIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Affected Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {affectedUsers}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center">
            <div
              className={`p-3 rounded-lg mr-4 ${
                forceLogoutEnabled ? "bg-red-100" : "bg-green-100"
              }`}
            >
              {forceLogoutEnabled ? (
                <PowerIcon className="h-6 w-6 text-red-600" />
              ) : (
                <ShieldCheckIcon className="h-6 w-6 text-green-600" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-600">Current Status</p>
              <p
                className={`text-xl font-semibold ${
                  forceLogoutEnabled ? "text-red-600" : "text-green-600"
                }`}
              >
                {forceLogoutEnabled ? "ENABLED" : "DISABLED"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-8">
          <div className="border-b border-gray-100 pb-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Force Logout Control
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              When enabled, updates all users' is_first_time field
            </p>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="flex-1">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900">
                    {forceLogoutEnabled ? "Force Mode Active" : "Normal Mode"}
                  </h3>
                  <p className="text-sm text-gray-600 mt-2">
                    {forceLogoutEnabled
                      ? `${affectedUsers} users will be forced to complete first-time setup on their next login.`
                      : "Users can login normally without additional requirements."}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    What happens:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                      Updates{" "}
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                        app_settings/force_update_info
                      </code>
                    </li>
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                      Updates{" "}
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                        is_first_time
                      </code>{" "}
                      for {affectedUsers} users
                    </li>
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                      Takes effect immediately on user login
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Toggle Section */}
            <div className="flex flex-col items-center gap-6">
              <div className="text-center">
                <button
                  onClick={handleToggle}
                  disabled={saving || updatingProgress !== null}
                  className={`
                    relative inline-flex h-10 w-20 items-center rounded-full transition-all
                    ${forceLogoutEnabled ? "bg-red-600" : "bg-gray-300"}
                    ${saving || updatingProgress ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:opacity-90"}
                  `}
                >
                  <span
                    className={`
                      inline-block h-8 w-8 transform rounded-full bg-white shadow-md transition-transform
                      ${forceLogoutEnabled ? "translate-x-11" : "translate-x-1"}
                    `}
                  />
                </button>
                <div className="flex justify-between w-full mt-2 text-sm text-gray-600">
                  <span>OFF</span>
                  <span>ON</span>
                </div>
              </div>

              <Button
                variant={forceLogoutEnabled ? "danger" : "primary"}
                onClick={handleToggle}
                loading={saving}
                disabled={saving || updatingProgress !== null}
                className="min-w-[180px]"
                leftIcon={
                  forceLogoutEnabled ? (
                    <ShieldCheckIcon className="h-5 w-5" />
                  ) : (
                    <PowerIcon className="h-5 w-5" />
                  )
                }
              >
                {saving ? (
                  updatingProgress ? (
                    <span className="flex items-center">
                      <LoadingSpinner
                        size="sm"
                        color="white"
                        className="mr-2"
                      />
                      Updating ({updatingProgress.current}/
                      {updatingProgress.total})
                    </span>
                  ) : (
                    "Processing..."
                  )
                ) : forceLogoutEnabled ? (
                  "Disable Force Logout"
                ) : (
                  "Enable Force Logout"
                )}
              </Button>

              {/* Status Messages */}
              {updatingProgress && (
                <div className="text-center">
                  <p className="text-sm text-blue-600">
                    Updating users... {updatingProgress.current}/
                    {updatingProgress.total}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    This may take a moment
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-6">
        <h3 className="font-medium text-blue-900 mb-3">
          Important Information
        </h3>
        <div className="space-y-3 text-sm text-blue-800">
          <p>
            • This action updates <strong>ALL users</strong> who have the{" "}
            <code className="bg-blue-100 px-1.5 py-0.5 rounded">
              is_first_time
            </code>{" "}
            field
          </p>
          <p>
            • Users will be <strong>immediately affected</strong> on their next
            login
          </p>
          <p>
            • Each user's{" "}
            <code className="bg-blue-100 px-1.5 py-0.5 rounded">
              is_first_time
            </code>{" "}
            field is updated to match the setting
          </p>
          <p className="mt-4 text-xs text-blue-700">
            Database updates:{" "}
            <code className="bg-blue-100 px-1.5 py-0.5 rounded">
              app_settings/force_update_info
            </code>{" "}
            +{" "}
            <code className="bg-blue-100 px-1.5 py-0.5 rounded">
              {affectedUsers} user documents
            </code>
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmToggle}
        title={pendingAction ? "Enable Force Logout" : "Disable Force Logout"}
        message={`Are you sure you want to ${
          pendingAction ? "enable" : "disable"
        } force logout?\n\nThis will update ${affectedUsers} users' 'is_first_time' field to ${pendingAction}.`}
        confirmText={pendingAction ? "Enable" : "Disable"}
        cancelText="Cancel"
        variant={pendingAction ? "warning" : "info"}
        isLoading={saving}
      />
    </div>
  );
};

export default Sessions;
